// ============================================================================
// CLEO'S SECRET — PrepPack
// File: src/pages/PrepPack.jsx
//
// Stage 2 patient surface, shown after the AI intake conversation completes.
// Displays a personalised pre-consultation pack assembled by /api/prep-pack:
// greeting, what to expect, investigations the GP may consider, how to
// prepare, questions to ask, reassurance.
//
// Migrated to canonical spec (Session 4, May 2026):
//   • Canonical plum + gold ramp via design-tokens
//   • Cormorant Garamond editorial (Georgia fallback)
//   • Stage 2 page-opener pattern: canonical Ankh 96 + PageOpener
//     (premise → title → subtitle, NOT eyebrow → headline)
//   • Canonical 9px diamond bullets via local BulletItem (Section 9)
//   • Sharp-cornered surfaces (radius 0); gold-edged inset for greeting +
//     reassurance, papyrus-on-sand cards for investigations
//   • Spinner + ErrorState atoms replace local loading/error chrome
//   • BtnPrimary + BtnGhost replace local rounded buttons
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, P, T,
  GeoDivider, Spinner, ErrorState,
  PageOpener, BtnPrimary, BtnGhost,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

export default function PrepPack(props) {
  var summary = props.summary;
  var intakeId = props.intakeId;
  var email = props.email;
  var onNavigate = props.onNavigate;

  var packState = useState(null);
  var pack = packState[0];
  var setPack = packState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  useEffect(function() {
    if (!summary) {
      setLoading(false);
      setError('No intake data found. Please complete your health conversation first.');
      return;
    }

    fetch('/api/prep-pack', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        summary: summary,
        intakeId: intakeId,
        email: email,
      }),
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      if (data.prepPack) setPack(data.prepPack);
      else setError('Could not generate your prep pack. Please try again.');
      setLoading(false);
    })
    .catch(function() {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    });
  }, [summary, intakeId, email]);

  // ───────── Section helper — canonical eyebrowSm in mahogany bronze ─────────
  function Section(sectionProps) {
    return (
      <div style={{ marginBottom: 'clamp(24px, 2.6vw, 32px)' }}>
        <div style={{
          ...T.eyebrowSm,
          color: P.BR2,
          marginBottom: 14,
        }}>{sectionProps.title}</div>
        {sectionProps.children}
      </div>
    );
  }

  // ───────── BulletItem — canonical 9px diamond marker ─────────
  function BulletItem(itemProps) {
    return (
      <div style={{
        position: 'relative',
        paddingLeft: 36,
        marginBottom: 18,
        fontFamily: FF,
        fontSize: 'clamp(16px, 1.4vw, 19px)',
        lineHeight: 1.7,
        color: P.TX,
      }}>
        <span style={{
          position: 'absolute',
          left: 12,
          top: '0.7em',
          width: 9,
          height: 9,
          background: P.BR,
          transform: 'rotate(45deg)',
        }} />
        {itemProps.children}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div style={{
        fontFamily: FF,
        background: P.SAND,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <Spinner color={P.GO} size={48} />
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          fontStyle: 'italic',
          color: P.BR2,
        }}>Preparing your personalised pack…</div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // ERROR STATE
  // ════════════════════════════════════════════════════════════════════════
  if (error) {
    return (
      <ErrorState
        premise="Something is missing."
        title={error}
        action={
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('intake'); }}>
            Start your conversation
          </BtnPrimary>
        }
      />
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // MAIN
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>

      {/* ═════════ Canonical Stage 2 page-opener ═════════ */}
      <header style={{
        textAlign: 'center',
        padding: 'clamp(48px, 6vw, 88px) clamp(20px, 4vw, 40px) clamp(28px, 3.4vw, 44px)',
        borderBottom: '0.5px solid ' + P.SAND3,
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 'clamp(24px, 3vw, 36px)',
        }}>
          <Ankh responsive="opener" color={P.GO} />
        </div>

        <PageOpener
          premise="Personalised for you."
          title="Your Cleo prep pack."
          subtitle="Ready for your first appointment."
          titleStyle={T.pageTitle}
        />
      </header>

      {/* ═════════ Pack body ═════════ */}
      <div className="prep-content" style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 4vw, 40px) clamp(48px, 6vw, 80px)',
      }}>

        {pack && pack.greeting && (
          <div style={{
            padding: 'clamp(20px, 2.4vw, 28px) clamp(22px, 2.6vw, 32px)',
            background: 'rgba(201, 169, 97, 0.06)',
            border: '1px solid ' + P.GO,
            borderLeft: '3px solid ' + P.GO,
            marginBottom: 'clamp(28px, 3vw, 36px)',
            fontFamily: FF,
            fontSize: 'clamp(16px, 1.4vw, 19px)',
            lineHeight: 1.75,
            color: P.TX,
            fontStyle: 'italic',
          }}>{pack.greeting}</div>
        )}

        <GeoDivider />

        {pack && pack.what_to_expect && (
          <Section title="What to expect at your consultation">
            {pack.what_to_expect.map(function(item, i) {
              return <BulletItem key={i}>{item}</BulletItem>;
            })}
          </Section>
        )}

        {pack && pack.possible_investigations && (
          <Section title="Investigations your GP may consider">
            {pack.possible_investigations.map(function(inv, i) {
              return (
                <div key={i} style={{
                  padding: 'clamp(14px, 1.6vw, 18px) clamp(16px, 1.8vw, 22px)',
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  marginBottom: 8,
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(15px, 1.3vw, 17px)',
                    fontWeight: 600,
                    color: P.INK,
                    marginBottom: 4,
                    lineHeight: 1.3,
                  }}>{inv.name}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    color: P.TL,
                    lineHeight: 1.65,
                  }}>{inv.why}</div>
                </div>
              );
            })}
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(13px, 1.1vw, 14px)',
              fontStyle: 'italic',
              color: P.BR2,
              marginTop: 12,
            }}>Your GP will decide which investigations are appropriate based on your consultation.</div>
          </Section>
        )}

        <GeoDivider />

        {pack && pack.how_to_prepare && (
          <Section title="How to prepare">
            {pack.how_to_prepare.map(function(item, i) {
              return <BulletItem key={i}>{item}</BulletItem>;
            })}
          </Section>
        )}

        {pack && pack.questions_to_consider && (
          <Section title="Questions you might want to ask">
            {pack.questions_to_consider.map(function(q, i) {
              return (
                <div key={i} style={{
                  display: 'flex',
                  gap: 14,
                  marginBottom: 18,
                  paddingLeft: 12,
                  fontFamily: FF,
                  fontSize: 'clamp(16px, 1.4vw, 19px)',
                  lineHeight: 1.7,
                  color: P.TX,
                }}>
                  <span style={{
                    fontFamily: FF,
                    fontStyle: 'italic',
                    fontSize: '1.15em',
                    fontWeight: 500,
                    color: P.BR2,
                    flexShrink: 0,
                    lineHeight: 1.7,
                  }}>?</span>
                  <span>{q}</span>
                </div>
              );
            })}
          </Section>
        )}

        <GeoDivider />

        {pack && pack.reassurance && (
          <div style={{
            padding: 'clamp(22px, 2.6vw, 32px) clamp(22px, 2.6vw, 32px)',
            background: 'rgba(201, 169, 97, 0.06)',
            border: '1px solid ' + P.GO,
            borderLeft: '3px solid ' + P.GO,
            marginBottom: 'clamp(32px, 3.6vw, 44px)',
            fontFamily: FF,
            fontSize: 'clamp(16px, 1.4vw, 19px)',
            lineHeight: 1.75,
            color: P.TX,
            textAlign: 'center',
          }}>{pack.reassurance}</div>
        )}

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          alignItems: 'center',
        }}>
          <BtnPrimary onClick={function() { window.print(); }}>Save as PDF</BtnPrimary>
          <BtnGhost onClick={function() { if (onNavigate) onNavigate('scheduler'); }}>
            Book your consultation
          </BtnGhost>
        </div>
      </div>

      <style>{'@media print { body { background: white !important; } button { display: none !important; } .prep-content { max-width: 100% !important; } }'}</style>
    </div>
  );
}
